import type { Tab } from '@revelio/data-access';

import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type { GlobalAssistantSuggestMatch } from './global-ai-assistant-api';
import type { GlobalAiSingleChartTranscriptMessage } from './global-ai-assistant-transcript';
import type { NavigateToOptionResult } from './global-ai-client-tool-deps';

const STANDARD_INDIVIDUAL_DISCOVERY_PAGE_ID = '__std__:app:talent-discovery';
const STANDARD_INDIVIDUAL_DISCOVERY_NAME = 'Individual Discovery';
const STANDARD_PAGE_LABEL_BY_SEGMENT: Record<string, string> = {
  summary: 'Overview',
  compositions: 'Compositions',
  transitions: 'Transitions',
  postings: 'Job Postings',
  pay: 'Pay',
  sentiment: 'Sentiment',
  screener: 'Discovery',
};
const STANDARD_ENTITY_LABEL_BY_SEGMENT: Record<string, string> = {
  company: 'Company',
  geography: 'Geography',
  role: 'Role',
};

export function toStandardPageLabel(standardPagePath: string): string {
  const segments = standardPagePath.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];

  if (firstSegment === 'talent-discovery') {
    return STANDARD_INDIVIDUAL_DISCOVERY_NAME;
  }
  if (
    !firstSegment ||
    !secondSegment ||
    !(firstSegment in STANDARD_ENTITY_LABEL_BY_SEGMENT)
  ) {
    return 'standard page';
  }
  const entityLabel = STANDARD_ENTITY_LABEL_BY_SEGMENT[firstSegment];
  const pageLabel = STANDARD_PAGE_LABEL_BY_SEGMENT[secondSegment];
  if (!entityLabel || !pageLabel) {
    return 'standard page';
  }
  return `${entityLabel} ${pageLabel}`;
}

function toStandardPageId(standardPagePath: string): string {
  const segments = standardPagePath.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];
  if (firstSegment === 'talent-discovery') {
    return STANDARD_INDIVIDUAL_DISCOVERY_PAGE_ID;
  }
  if (!firstSegment || !secondSegment) {
    return '__std__:custom:standard-page';
  }
  return `__std__:${firstSegment}:${secondSegment}`;
}

function isIndividualDiscoveryPath(standardPagePath: string): boolean {
  return standardPagePath.split('/').filter(Boolean)[0] === 'talent-discovery';
}

export function getSingleChartStandardPageButtonLabel(
  standardPagePath: string | undefined
): string {
  const normalizedPath = standardPagePath?.trim();
  if (!normalizedPath) {
    return 'Go to standard page';
  }
  return `Go to ${toStandardPageLabel(normalizedPath)}`;
}

const STANDARD_APP_LABEL_BY_SEGMENT: Record<string, string> = {
  'talent-discovery': STANDARD_INDIVIDUAL_DISCOVERY_NAME,
  'data-dictionary': 'Data Dictionary',
};

/**
 * Invert a `__std__:{entity}:{pathSegment}` candidate id (the turn loop's
 * `navigate.optionId`, produced by the backend canonical page intents) into a
 * navigable suggest match. `app`-entity ids map to a top-level route
 * (`/${pathSegment}`); entity ids map to `/${entity}/${pathSegment}`. Returns
 * null for an id that doesn't parse as a standard option.
 */
export function getStandardPageMatchForOptionId(
  optionId: string,
  options?: { resolvedPromptForApply?: string }
): GlobalAssistantSuggestMatch | null {
  const match = optionId.trim().match(/^__std__:([a-z]+):([a-z0-9-]+)$/i);
  if (!match) {
    return null;
  }
  const entity = match[1]?.toLowerCase();
  const segment = match[2]?.toLowerCase();
  if (!entity || !segment) {
    return null;
  }
  if (entity === 'app') {
    const navigatePath = `/${segment}`;
    const isIndividualDiscovery = segment === 'talent-discovery';
    return {
      pageId: optionId,
      name: STANDARD_APP_LABEL_BY_SEGMENT[segment] ?? segment,
      navigatePath,
      confidenceScore: 100,
      ...(isIndividualDiscovery
        ? {
            discoveryKind: 'talent' as const,
            applyFiltersAfterNavigate: true,
          }
        : {}),
      ...(options?.resolvedPromptForApply
        ? { resolvedPromptForApply: options.resolvedPromptForApply }
        : {}),
    };
  }
  const entityLabel = STANDARD_ENTITY_LABEL_BY_SEGMENT[entity];
  const pageLabel = STANDARD_PAGE_LABEL_BY_SEGMENT[segment];
  if (!entityLabel || !pageLabel) {
    return null;
  }
  return {
    pageId: optionId,
    name: `${entityLabel} ${pageLabel}`,
    navigatePath: `/${entity}/${segment}`,
    confidenceScore: 100,
    ...(options?.resolvedPromptForApply
      ? { resolvedPromptForApply: options.resolvedPromptForApply }
      : {}),
  };
}

/**
 * Resolve a loop `navigate` candidate id into a discriminated outcome,
 * enforcing the same route-access/entitlement gate the manual chip uses
 * (FE-2117). The caller supplies `performNavigate`, which actually drives the
 * router (via `navigateToMatch`) and reports whether navigation proceeded —
 * the unsaved-changes redirect guard can cancel it. Returns:
 *  - `{ navigatedTo }` on success
 *  - `notNavigated` with reason `unknown_page` (id didn't map to a page),
 *    `no_access` (+ featureLabel/message, entitlement denied), or `cancelled`
 *    (redirect guard cancelled).
 * Keeping this pure makes the access/cancel/unknown branches unit-testable
 * without a full component render.
 */
export async function resolveNavigateToOptionId(
  optionId: string,
  userTabs: Tab[],
  // Async since navigateToMatch became resolve-first (awaits filter
  // resolution before navigating); a sync performNavigate still works —
  // its plain return value is awaited transparently.
  performNavigate: (
    match: GlobalAssistantSuggestMatch
  ) => Promise<{ navigated: boolean }> | { navigated: boolean }
): Promise<NavigateToOptionResult> {
  const match = getStandardPageMatchForOptionId(optionId, {});
  if (!match) {
    return { notNavigated: true, reason: 'unknown_page' };
  }
  const access = getGlobalAssistantRouteAccess(match.navigatePath, userTabs);
  if (!access.allowed) {
    return {
      notNavigated: true,
      reason: 'no_access',
      featureLabel: access.featureLabel,
      message: access.message,
    };
  }
  const { navigated } = await performNavigate(match);
  if (!navigated) {
    return { notNavigated: true, reason: 'cancelled' };
  }
  return { navigatedTo: match.name };
}

export function getSingleChartStandardPageMatch(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAssistantSuggestMatch | null {
  const standardPagePath = msg.standardPagePath?.trim();
  if (!standardPagePath) {
    return null;
  }
  const isIndividualDiscovery = isIndividualDiscoveryPath(standardPagePath);
  const maybeResolvedPrompt = msg.prompt?.trim();
  return {
    pageId: toStandardPageId(standardPagePath),
    name: toStandardPageLabel(standardPagePath),
    navigatePath: standardPagePath,
    confidenceScore: 100,
    ...(isIndividualDiscovery
      ? {
          discoveryKind: 'talent' as const,
          applyFiltersAfterNavigate: true,
        }
      : {}),
    ...(maybeResolvedPrompt
      ? { resolvedPromptForApply: maybeResolvedPrompt }
      : {}),
  };
}
